export interface ProductVariant {
  code: string;
  spec: string;
  price: number;
  audPrice: number;
}

export interface ProductFamily {
  id: string;
  name: string;
  categories: string[];
  variants: ProductVariant[];
}

export const CATEGORIES = [
  { id: "all", label: "All", emoji: "🧬" },
  { id: "glp-1", label: "GLP-1", emoji: "💉" },
  { id: "hgh", label: "HGH", emoji: "💪" },
  { id: "repair", label: "Repair", emoji: "🔧" },
  { id: "anti-aging", label: "Anti-Aging", emoji: "⏳" },
  { id: "immune-neuro", label: "Immune/Neuro", emoji: "🛡" },
  { id: "other", label: "Other", emoji: "📋" },
] as const;

/**
 * Master catalog source: 4倍调价.xlsx / ATT + user-supplied supplemental product rows.
 * The website catalog intentionally mirrors the spreadsheet:
 * - spreadsheet-only products are included;
 * - website-only products are not included;
 * - USD prices use the whole-dollar values displayed in the spreadsheet;
 * - AUD prices are derived at the site's fixed 1 USD = 1.42 AUD display multiplier.
 */
export const products: ProductFamily[] = [
  {
    id: "semaglutide",
    name: "Semaglutide",
    categories: ["glp-1"],
    variants: [
      { code: "SM5", spec: "5mg×10vials", price: 74, audPrice: 105 },
      { code: "SM10", spec: "10mg×10vials", price: 132, audPrice: 187 },
      { code: "SM15", spec: "15mg×10vials", price: 152, audPrice: 216 },
      { code: "SM20", spec: "20mg×10vials", price: 180, audPrice: 256 },
      { code: "SM30", spec: "30mg×10vials", price: 232, audPrice: 329 },
      { code: "SM40", spec: "40mg×10vials", price: 251, audPrice: 356 },
      { code: "SM50", spec: "50mg×10vials", price: 283, audPrice: 402 },
    ],
  },
  {
    id: "retatrutide",
    name: "Retatrutide",
    categories: ["glp-1"],
    variants: [
      { code: "RT5", spec: "5mg×10vials", price: 56, audPrice: 80 },
      { code: "RT10", spec: "10mg×10vials", price: 81, audPrice: 115 },
      { code: "RT15", spec: "15mg×10vials", price: 120, audPrice: 170 },
      { code: "RT20", spec: "20mg×10vials", price: 152, audPrice: 216 },
      { code: "RT30", spec: "30mg×10vials", price: 173, audPrice: 246 },
      { code: "RT40", spec: "40mg×10vials", price: 202, audPrice: 287 },
      { code: "RT50", spec: "50mg×10vials", price: 314, audPrice: 446 },
      { code: "RT60", spec: "60mg×10vials", price: 365, audPrice: 518 },
      { code: "RT100", spec: "100mg×10vials", price: 586, audPrice: 832 },
    ],
  },
  {
    id: "tirzepatide",
    name: "Tirzepatide",
    categories: ["glp-1"],
    variants: [
      { code: "TR5", spec: "5mg×10vials", price: 41, audPrice: 58 },
      { code: "TR10", spec: "10mg×10vials", price: 58, audPrice: 82 },
      { code: "TR15", spec: "15mg×10vials", price: 73, audPrice: 104 },
      { code: "TR20", spec: "20mg×10vials", price: 89, audPrice: 126 },
      { code: "TR30", spec: "30mg×10vials", price: 101, audPrice: 143 },
      { code: "TR40", spec: "40mg×10vials", price: 128, audPrice: 182 },
      { code: "TR50", spec: "50mg×10vials", price: 157, audPrice: 223 },
      { code: "TR60", spec: "60mg×10vials", price: 193, audPrice: 274 },
      { code: "TR100", spec: "100mg×10vials", price: 304, audPrice: 432 },
    ],
  },
  {
    id: "nad",
    name: "NAD+",
    categories: ["anti-aging"],
    variants: [
      { code: "NJ100", spec: "100mg×10vials", price: 40, audPrice: 57 },
      { code: "NJ500", spec: "500mg×10vials", price: 59, audPrice: 84 },
      { code: "NJ1000", spec: "1000mg×10vials", price: 77, audPrice: 109 },
    ],
  },
  {
    id: "tesamorelin",
    name: "Tesamorelin",
    categories: ["hgh"],
    variants: [
      { code: "TSM2", spec: "2mg×10vials", price: 87, audPrice: 124 },
      { code: "TSM5", spec: "5mg×10vials", price: 193, audPrice: 274 },
      { code: "TSM10", spec: "10mg×10vials", price: 313, audPrice: 444 },
      { code: "TSM20", spec: "20mg×10vials", price: 526, audPrice: 747 },
    ],
  },
  {
    id: "ghk-cu",
    name: "GHK-CU",
    categories: ["repair", "anti-aging"],
    variants: [
      { code: "CU50", spec: "50mg×10vials", price: 37, audPrice: 53 },
      { code: "CU100", spec: "100mg×10vials", price: 59, audPrice: 84 },
    ],
  },
  {
    id: "tb500-thymosin-b4-acetate",
    name: "TB500(Thymosin B4 Acetate",
    categories: ["repair"],
    variants: [
      { code: "BT2", spec: "2mg×10vials", price: 65, audPrice: 92 },
      { code: "BT5", spec: "5mg×10vials", price: 134, audPrice: 190 },
      { code: "BT10", spec: "10mg×10vials", price: 253, audPrice: 359 },
    ],
  },
  {
    id: "bpc-157",
    name: "BPC 157",
    categories: ["repair"],
    variants: [
      { code: "BC2", spec: "2mg×10vials", price: 27, audPrice: 38 },
      { code: "BC5", spec: "5mg×10vials", price: 46, audPrice: 65 },
      { code: "BC10", spec: "10mg×10vials", price: 71, audPrice: 101 },
      { code: "BC20", spec: "20mg×10vials", price: 128, audPrice: 182 },
    ],
  },
  {
    id: "mots-c",
    name: "MOTS-C",
    categories: ["anti-aging", "immune-neuro"],
    variants: [
      { code: "MS10", spec: "10mg×10vials", price: 109, audPrice: 155 },
      { code: "MS20", spec: "20mg×10vials", price: 203, audPrice: 288 },
      { code: "MS40", spec: "40mg×10vials", price: 369, audPrice: 524 },
    ],
  },
  {
    id: "bpc5mg-tb5mg",
    name: "BPC5mg+TB5mg",
    categories: ["repair"],
    variants: [
      { code: "BB10", spec: "10mg×10vials", price: 134, audPrice: 190 },
    ],
  },
  {
    id: "bpc10mg-tb10mg",
    name: "BPC10mg+TB10mg",
    categories: ["repair"],
    variants: [
      { code: "BB20", spec: "20mg×10vials", price: 325, audPrice: 462 },
    ],
  },
  {
    id: "lysine-proline-valine-kpv",
    name: "LYSINE-PROLINE VALINE(KPV)",
    categories: ["repair"],
    variants: [
      { code: "KP5", spec: "5mg×10vials", price: 40, audPrice: 57 },
      { code: "KP10", spec: "10mg×10vials", price: 74, audPrice: 105 },
    ],
  },
  {
    id: "ipamorelin",
    name: "Ipamorelin",
    categories: ["hgh"],
    variants: [
      { code: "IP2", spec: "2mg×10vials", price: 27, audPrice: 38 },
      { code: "IP5", spec: "5mg×10vials", price: 49, audPrice: 70 },
      { code: "IP10", spec: "10mg×10vials", price: 81, audPrice: 115 },
    ],
  },
  {
    id: "selank",
    name: "Selank",
    categories: ["immune-neuro"],
    variants: [
      { code: "SK5", spec: "5mg×10vials", price: 55, audPrice: 78 },
      { code: "SK10", spec: "10mg×10vials", price: 87, audPrice: 124 },
    ],
  },
  {
    id: "cjc-1295-no-dac-5mg-ipa5mg",
    name: "CJC-1295 no DAC 5mg+IPA5mg",
    categories: ["hgh"],
    variants: [
      { code: "CP10", spec: "10mg×10vials", price: 146, audPrice: 207 },
    ],
  },
  {
    id: "cjc-1295-with-dac",
    name: "CJC-1295 with DAC",
    categories: ["hgh"],
    variants: [
      { code: "CD2", spec: "2mg×10vials", price: 165, audPrice: 234 },
      { code: "CD5", spec: "5mg×10vials", price: 319, audPrice: 453 },
      { code: "CD10", spec: "10mg×10vials", price: 494, audPrice: 701 },
    ],
  },
  {
    id: "cjc-1295-no-dac",
    name: "CJC-1295 no DAC",
    categories: ["hgh"],
    variants: [
      { code: "CND2", spec: "2mg×10vials", price: 52, audPrice: 74 },
      { code: "CND5", spec: "5mg×10vials", price: 96, audPrice: 136 },
      { code: "CND10", spec: "10mg×10vials", price: 175, audPrice: 249 },
    ],
  },
  {
    id: "ghk-cu-tb500",
    name: "GHK-cu+TB500复合肽",
    categories: ["repair"],
    variants: [
      { code: "BBG70", spec: "70mg×10vials", price: 350, audPrice: 497 },
    ],
  },
  {
    id: "cu50-tb10-bc10-kpv10",
    name: "复合肽 (CU50+TB10+BC10+KPV10)",
    categories: ["repair"],
    variants: [
      { code: "KLW80", spec: "80mg×10vials", price: 344, audPrice: 488 },
    ],
  },
  {
    id: "ss-31",
    name: "SS-31",
    categories: ["anti-aging"],
    variants: [
      { code: "2S10", spec: "10mg×10vials", price: 131, audPrice: 186 },
      { code: "2S50", spec: "50mg×10vials", price: 526, audPrice: 747 },
    ],
  },
  {
    id: "dsip",
    name: "DSIP",
    categories: ["immune-neuro"],
    variants: [
      { code: "DS2", spec: "2mg×10vials", price: 59, audPrice: 84 },
      { code: "DS5", spec: "5mg×10vials", price: 71, audPrice: 101 },
      { code: "DS10", spec: "10mg×10vials", price: 121, audPrice: 172 },
    ],
  },
  {
    id: "pt-141",
    name: "PT-141",
    categories: ["immune-neuro"],
    variants: [
      { code: "PT141", spec: "10mg×10vials", price: 102, audPrice: 145 },
    ],
  },
  {
    id: "slu-pp-332",
    name: "SLU-PP-332",
    categories: ["other"],
    variants: [
      { code: "332", spec: "5mg×10vials", price: 77, audPrice: 109 },
    ],
  },
  {
    id: "aicar-aicar",
    name: "Aicar(AICAR）",
    categories: ["immune-neuro"],
    variants: [
      { code: "AR50", spec: "50mg×10vials", price: 62, audPrice: 88 },
      { code: "AR100", spec: "100mg×10vials", price: 106, audPrice: 151 },
    ],
  },
  {
    id: "hgh-191aa-somatropin",
    name: "HGH 191AA(Somatropin)",
    categories: ["hgh"],
    variants: [
      { code: "H10", spec: "10iu×10vials", price: 71, audPrice: 101 },
      { code: "H12", spec: "12iu×10vials", price: 96, audPrice: 136 },
      { code: "H15", spec: "15iu×10vials", price: 128, audPrice: 182 },
      { code: "H24", spec: "24iu×10vials", price: 175, audPrice: 249 },
      { code: "H36", spec: "36iu×10vials", price: 247, audPrice: 351 },
    ],
  },
  {
    id: "thymosin-alpha-1",
    name: "Thymosin Alpha-1",
    categories: ["immune-neuro"],
    variants: [
      { code: "TA5", spec: "5mg×10vials", price: 159, audPrice: 226 },
      { code: "TA10", spec: "10mg×10vials", price: 262, audPrice: 372 },
    ],
  },
  {
    id: "aod9604",
    name: "AOD9604",
    categories: ["other"],
    variants: [
      { code: "2AD", spec: "2mg×10vials", price: 65, audPrice: 92 },
      { code: "5AD", spec: "5mg×10vials", price: 153, audPrice: 217 },
      { code: "10AD", spec: "10mg×10vials", price: 284, audPrice: 403 },
    ],
  },
  {
    id: "5amino-mq",
    name: "5Amino/MQ",
    categories: ["other"],
    variants: [
      { code: "5AM05", spec: "5mg×10vials", price: 46, audPrice: 65 },
      { code: "5AM10", spec: "10mg×10vials", price: 93, audPrice: 132 },
      { code: "50AM", spec: "50mg×10vials", price: 212, audPrice: 301 },
    ],
  },
  {
    id: "kisspeptin-10",
    name: "KissPeptin-10",
    categories: ["other"],
    variants: [
      { code: "KS5", spec: "5mg×10vials", price: 84, audPrice: 119 },
      { code: "KS10", spec: "10mg×10vials", price: 146, audPrice: 207 },
    ],
  },
  {
    id: "epithalon",
    name: "Epithalon",
    categories: ["anti-aging"],
    variants: [
      { code: "ET10", spec: "10mg×10vials", price: 59, audPrice: 84 },
      { code: "ET50", spec: "50mg×10vials", price: 247, audPrice: 351 },
    ],
  },
  {
    id: "melanotan-1",
    name: "Melanotan 1",
    categories: ["other"],
    variants: [
      { code: "MT-1", spec: "10mg×10vials", price: 77, audPrice: 109 },
      { code: "MT-2", spec: "10mg×10vials", price: 77, audPrice: 109 },
      { code: "MT-10", spec: "10mg×10vials", price: 77, audPrice: 109 },
    ],
  },
  {
    id: "hcg",
    name: "HCG",
    categories: ["hgh"],
    variants: [
      { code: "G1K", spec: "1000iu×10vials", price: 90, audPrice: 128 },
      { code: "G2K", spec: "2000iu×10vials", price: 115, audPrice: 163 },
      { code: "G5K", spec: "5000iu×10vials", price: 209, audPrice: 297 },
      { code: "G10K", spec: "10000iu×10vials", price: 328, audPrice: 466 },
    ],
  },
  {
    id: "sermorelin",
    name: "Sermorelin",
    categories: ["hgh"],
    variants: [
      { code: "SMO5", spec: "5mg×10vials", price: 134, audPrice: 190 },
      { code: "SMO10", spec: "10mg×10vials", price: 247, audPrice: 351 },
    ],
  },
  {
    id: "admax",
    name: "Admax",
    categories: ["anti-aging"],
    variants: [
      { code: "AX5", spec: "5mg×10vials", price: 77, audPrice: 109 },
      { code: "AX10", spec: "10mg×10vials", price: 121, audPrice: 172 },
    ],
  },
  {
    id: "lemon-bottle",
    name: "Lemon Bottle",
    categories: ["other"],
    variants: [
      { code: "LB", spec: "10ml×10vials", price: 84, audPrice: 119 },
    ],
  },
  {
    id: "igf-1lr3",
    name: "IGF-1LR3",
    categories: ["hgh"],
    variants: [
      { code: "IG01", spec: "0.1mg×10vials", price: 49, audPrice: 70 },
      { code: "IG1", spec: "1mg×10vials", price: 363, audPrice: 515 },
    ],
  },
  {
    id: "mazdutide",
    name: "Mazdutide",
    categories: ["glp-1"],
    variants: [
      { code: "MZ5", spec: "5mg×10vials", price: 187, audPrice: 266 },
      { code: "MZ10", spec: "10mg×10vials", price: 369, audPrice: 524 },
    ],
  },
  {
    id: "survovidutide",
    name: "Survovidutide",
    categories: ["glp-1"],
    variants: [
      { code: "SUR10.00", spec: "10mg×10vials", price: 485, audPrice: 689 },
    ],
  },
  {
    id: "cagri-2-5mg-semaglutide2-5mg",
    name: "Cagri 2.5mg+Semaglutide2.5mg",
    categories: ["glp-1"],
    variants: [
      { code: "CS5", spec: "5mg×10vials", price: 140, audPrice: 199 },
      { code: "CS10", spec: "10mg×10vials", price: 259, audPrice: 368 },
    ],
  },
  {
    id: "cagriliintide",
    name: "Cagriliintide",
    categories: ["glp-1"],
    variants: [
      { code: "CGL5", spec: "5mg×10vials", price: 203, audPrice: 288 },
      { code: "CGL10", spec: "10mg×10vials", price: 378, audPrice: 537 },
    ],
  },
  {
    id: "adipotide",
    name: "Adipotide",
    categories: ["other"],
    variants: [
      { code: "AP2", spec: "2mg×10vials", price: 96, audPrice: 136 },
      { code: "AP5", spec: "5mg×10vials", price: 215, audPrice: 305 },
    ],
  },
  {
    id: "ara290",
    name: "ARA290",
    categories: ["other"],
    variants: [
      { code: "RA10", spec: "10mg×10vials", price: 96, audPrice: 136 },
      { code: "RA16", spec: "16mg×10vials", price: 146, audPrice: 207 },
    ],
  },
  {
    id: "gdf-8",
    name: "GDF-8",
    categories: ["hgh"],
    variants: [
      { code: "GDF", spec: "1mg×10vials", price: 303, audPrice: 430 },
    ],
  },
  {
    id: "thymalin",
    name: "Thymalin",
    categories: ["immune-neuro"],
    variants: [
      { code: "TY10", spec: "10mg×10vials", price: 102, audPrice: 145 },
    ],
  },
  {
    id: "cagrilintide",
    name: "Cagrilintide",
    categories: ["glp-1"],
    variants: [
      { code: "CG05", spec: "5mg×10vials", price: 234, audPrice: 332 },
      { code: "CG10", spec: "10mg×10vials", price: 385, audPrice: 547 },
    ],
  },
  {
    id: "ace-031",
    name: "ACE 031",
    categories: ["hgh"],
    variants: [
      { code: "AC1", spec: "1mg×10vials", price: 84, audPrice: 119 },
    ],
  },
  {
    id: "ghrp-2-acetate",
    name: "GHRP-2 Acetate",
    categories: ["hgh"],
    variants: [
      { code: "G25", spec: "5mg×10vials", price: 43, audPrice: 61 },
      { code: "G210", spec: "10mg×10vials", price: 59, audPrice: 84 },
    ],
  },
  {
    id: "ghrp-6-acetate",
    name: "GHRP-6 Acetate",
    categories: ["hgh"],
    variants: [
      { code: "G65", spec: "5mg×10vials", price: 40, audPrice: 57 },
      { code: "G610", spec: "10mg×10vials", price: 65, audPrice: 92 },
    ],
  },
  {
    id: "glutathione",
    name: "Glutathione",
    categories: ["other"],
    variants: [
      { code: "ATT600", spec: "600mg×10vials", price: 59, audPrice: 84 },
      { code: "ATT1500", spec: "1500mg×10vials", price: 96, audPrice: 136 },
    ],
  },
  {
    id: "acth-1-39",
    name: "ACTH 1-39",
    categories: ["other"],
    variants: [
      { code: "ACTH1", spec: "5mg×10vials", price: 109, audPrice: 155 },
    ],
  },
  {
    id: "frag-17-23",
    name: "Frag 17-23",
    categories: ["hgh"],
    variants: [
      { code: "FRAG10", spec: "10mg×10vials", price: 140, audPrice: 199 },
    ],
  },
  {
    id: "foxo4",
    name: "FOXO4",
    categories: ["anti-aging"],
    variants: [
      { code: "F42", spec: "2mg×10vials", price: 159, audPrice: 226 },
    ],
  },
  {
    id: "foxo4-dri",
    name: "FOXO4-DRI",
    categories: ["hgh"],
    variants: [
      { code: "F410", spec: "10mg×10vials", price: 686, audPrice: 974 },
    ],
  },
  {
    id: "dulaglutide",
    name: "Dulaglutide",
    categories: ["glp-1"],
    variants: [
      { code: "DUL05", spec: "5mg×10vials", price: 234, audPrice: 332 },
      { code: "DUL10", spec: "10mg×10vials", price: 369, audPrice: 524 },
    ],
  },
  {
    id: "liraglutide",
    name: "Liraglutide",
    categories: ["glp-1"],
    variants: [
      { code: "LIR5", spec: "5mg×10vials", price: 140, audPrice: 199 },
      { code: "LIR10", spec: "10mg×10vials", price: 234, audPrice: 332 },
      { code: "LIR30", spec: "30mg×10vials", price: 604, audPrice: 858 },
    ],
  },
  {
    id: "dihexa",
    name: "Dihexa",
    categories: ["immune-neuro"],
    variants: [
      { code: "DIH05", spec: "5mg×10vials", price: 96, audPrice: 136 },
      { code: "DIH10", spec: "10mg×10vials", price: 146, audPrice: 207 },
    ],
  },
  {
    id: "hgh-fragment-176-191",
    name: "HGH Fragment 176- 191",
    categories: ["hgh"],
    variants: [
      { code: "FR1", spec: "1mg×10vials", price: 77, audPrice: 109 },
      { code: "FR2", spec: "2mg×10vials", price: 96, audPrice: 136 },
      { code: "FR5", spec: "5mg×10vials", price: 159, audPrice: 226 },
      { code: "FR10", spec: "10mg×10vials", price: 253, audPrice: 359 },
      { code: "FR12", spec: "12mg×10vials", price: 291, audPrice: 413 },
      { code: "FR15", spec: "15mg×10vials", price: 341, audPrice: 484 },
    ],
  },
  {
    id: "orexin-b",
    name: "Orexin B",
    categories: ["immune-neuro"],
    variants: [
      { code: "OB5", spec: "5mg×10vials", price: 190, audPrice: 270 },
      { code: "OB10", spec: "10mg×10vials", price: 347, audPrice: 493 },
    ],
  },
  {
    id: "orexin-a",
    name: "Orexin A",
    categories: ["immune-neuro"],
    variants: [
      { code: "OA10", spec: "10mg×10vials", price: 802, audPrice: 1139 },
    ],
  },
  {
    id: "mazdutide-2",
    name: "Mazdutide",
    categories: ["glp-1"],
    variants: [
      { code: "MDT10", spec: "10mg×10vials", price: 360, audPrice: 511 },
    ],
  },
  {
    id: "fst-344",
    name: "FOLIESTATINA (FST-344)",
    categories: ["hgh"],
    variants: [
      { code: "FN1*1", spec: "1mg×10vials", price: 180, audPrice: 256 },
    ],
  },
  {
    id: "demorphin",
    name: "Demorphin",
    categories: ["immune-neuro"],
    variants: [
      { code: "DEM02", spec: "2mg×10vials", price: 71, audPrice: 101 },
    ],
  },
  {
    id: "pinealon",
    name: "Pinealon",
    categories: ["immune-neuro"],
    variants: [
      { code: "PIN5", spec: "5mg×10vials", price: 96, audPrice: 136 },
      { code: "PIN10", spec: "10mg×10vials", price: 165, audPrice: 234 },
    ],
  },
  {
    id: "p210",
    name: "P210",
    categories: ["immune-neuro"],
    variants: [
      { code: "P210", spec: "5mg×10vials", price: 485, audPrice: 689 },
    ],
  },
  {
    id: "hmg",
    name: "HMG",
    categories: ["hgh"],
    variants: [
      { code: "G75", spec: "75iu×10vials", price: 90, audPrice: 128 },
    ],
  },
  {
    id: "triptorelln-acetate",
    name: "Triptorelln Acetate",
    categories: ["other"],
    variants: [
      { code: "Triptorelln", spec: "2mg×10vials", price: 90, audPrice: 128 },
    ],
  },
  {
    id: "semax",
    name: "Semax",
    categories: ["immune-neuro"],
    variants: [
      { code: "XA5", spec: "5mg×10vials", price: 55, audPrice: 78 },
      { code: "XA10", spec: "10mg×10vials", price: 87, audPrice: 124 },
      { code: "SX10", spec: "10mg×10vials", price: 166, audPrice: 236 },
      { code: "SX20", spec: "20mg×10vials", price: 192, audPrice: 273 },
    ],
  },
  {
    id: "gonadorelin-acetate",
    name: "Gonadorelin Acetate",
    categories: ["other"],
    variants: [
      { code: "GND2", spec: "2mg×10vials", price: 33, audPrice: 47 },
    ],
  },
  {
    id: "hexarelin-acetate",
    name: "Hexarelin Acetate",
    categories: ["hgh"],
    variants: [
      { code: "HX2", spec: "2mg×10vials", price: 68, audPrice: 97 },
      { code: "HX5", spec: "5mg×10vials", price: 128, audPrice: 182 },
    ],
  },
  {
    id: "oxytocin-acetate",
    name: "Oxytocin Acetate",
    categories: ["immune-neuro"],
    variants: [
      { code: "OT2", spec: "2mg×10vials", price: 27, audPrice: 38 },
      { code: "OT5", spec: "5mg×10vials", price: 59, audPrice: 84 },
      { code: "OT10", spec: "10mg×10vials", price: 109, audPrice: 155 },
    ],
  },
  {
    id: "humanin",
    name: "Humanin",
    categories: ["hgh"],
    variants: [
      { code: "Humanin", spec: "10mg×10vials", price: 435, audPrice: 618 },
    ],
  },
  {
    id: "dermorphin",
    name: "Dermorphin",
    categories: ["immune-neuro"],
    variants: [
      { code: "DR5", spec: "5mg×10vials", price: 90, audPrice: 128 },
    ],
  },
  {
    id: "snap-8",
    name: "Snap-8",
    categories: ["anti-aging"],
    variants: [
      { code: "NP810", spec: "10mg×10vials", price: 46, audPrice: 65 },
    ],
  },
  {
    id: "cbl60",
    name: "CBL60",
    categories: ["immune-neuro"],
    variants: [
      { code: "CBL60", spec: "60mg×10vials", price: 134, audPrice: 190 },
    ],
  },
  {
    id: "mic-lipo-c-with-b12",
    name: "MIC(lipo C with B12)",
    categories: ["other"],
    variants: [
      { code: "MIC", spec: "10mg×10vials", price: 175, audPrice: 249 },
    ],
  },
  {
    id: "ll37",
    name: "LL37",
    categories: ["anti-aging"],
    variants: [
      { code: "375", spec: "5mg×10vials", price: 159, audPrice: 226 },
    ],
  },
  {
    id: "pnc-27",
    name: "PNC-27",
    categories: ["anti-aging"],
    variants: [
      { code: "PN5", spec: "5mg×10vials", price: 84, audPrice: 119 },
      { code: "PN10", spec: "10mg×10vials", price: 159, audPrice: 226 },
    ],
  },
  {
    id: "vip",
    name: "VIP",
    categories: ["immune-neuro"],
    variants: [
      { code: "VP5", spec: "5mg×10vials", price: 128, audPrice: 182 },
      { code: "VP10", spec: "10mg×10vials", price: 228, audPrice: 324 },
    ],
  },
  {
    id: "mgf",
    name: "MGF",
    categories: ["hgh"],
    variants: [
      { code: "FM2", spec: "2mg×10vials", price: 59, audPrice: 84 },
    ],
  },
  {
    id: "pe-22-28",
    name: "PE-22-28",
    categories: ["immune-neuro"],
    variants: [
      { code: "PE5", spec: "5mg×10vials", price: 77, audPrice: 109 },
      { code: "PE10", spec: "10mg×10vials", price: 146, audPrice: 207 },
    ],
  },
  {
    id: "peg-mgf",
    name: "PEG MGF",
    categories: ["hgh"],
    variants: [
      { code: "FMP2", spec: "2mg×10vials", price: 146, audPrice: 207 },
    ],
  },
  {
    id: "retatrutide-20mg-trizepatide-40mg",
    name: "Retatrutide 20mg +Trizepatide 40mg",
    categories: ["glp-1"],
    variants: [
      { code: "RET60", spec: "60mg×10vials", price: 425, audPrice: 604 },
    ],
  },
  {
    id: "bac-water-wac",
    name: "BAC water无菌水 WAC",
    categories: ["other"],
    variants: [
      { code: "WA3", spec: "3ml×10vials", price: 11, audPrice: 16 },
    ],
  },
  {
    id: "benzyl-alcohol-0-9-bac",
    name: "Benzyl Alcohol 0.9%抑菌水 BAC",
    categories: ["other"],
    variants: [
      { code: "BA3", spec: "3ml×10vials", price: 11, audPrice: 16 },
    ],
  },
  {
    id: "benzyl-alcohol-0-10-bac",
    name: "Benzyl Alcohol 0.10%抑菌水 BAC",
    categories: ["other"],
    variants: [
      { code: "BA10", spec: "3ml×11vials", price: 27, audPrice: 38 },
    ],
  },
  {
    id: "adamax",
    name: "Adamax",
    categories: ["anti-aging"],
    variants: [
      { code: "AD5", spec: "5mg×10vials", price: 193, audPrice: 274 },
      { code: "AD10", spec: "10mg×10vials", price: 269, audPrice: 382 },
    ],
  },
  {
    id: "retatrutide-cagrilintide",
    name: "Retatrutide + Cagrilintide",
    categories: ["glp-1"],
    variants: [
      { code: "RC10", spec: "10mg×10vials", price: 184, audPrice: 261 },
    ],
  },
  {
    id: "tesamorelin-ipamorelin",
    name: "Tesamorelin + Ipamorelin",
    categories: ["hgh"],
    variants: [
      { code: "TP2", spec: "2mg×10vials", price: 272, audPrice: 386 },
      { code: "TP5", spec: "5mg×10vials", price: 425, audPrice: 604 },
      { code: "TP10", spec: "10mg×10vials", price: 717, audPrice: 1018 },
    ],
  },
  {
    id: "lipo-c",
    name: "Lipo-c",
    categories: ["other"],
    variants: [
      { code: "LC216", spec: "10ml×10vials", price: 134, audPrice: 190 },
    ],
  },
  {
    id: "hcg-2",
    name: "HCG",
    categories: ["hgh"],
    variants: [
      { code: "HCG1000", spec: "1000iu×10vials", price: 77, audPrice: 109 },
      { code: "HCG2000", spec: "2000iu×10vials", price: 128, audPrice: 182 },
      { code: "HCG5000", spec: "5000iu×10vials", price: 193, audPrice: 274 },
      { code: "HCG10000", spec: "10000iu×10vials", price: 328, audPrice: 466 },
    ],
  },
  {
    id: "l-carnitine",
    name: "L-carnitine",
    categories: ["other"],
    variants: [
      { code: "LC600", spec: "10ml×10vials", price: 112, audPrice: 159 },
      { code: "LC1200", spec: "10ml×10vials", price: 200, audPrice: 284 },
    ],
  },
  {
    id: "dihaxa-pnb0408",
    name: "Dihaxa | PNB0408",
    categories: ["immune-neuro"],
    variants: [
      { code: "PNB0408", spec: "10mg×10vials", price: 132, audPrice: 187 },
    ],
  },
  {
    id: "bacteriostatic-water-b12",
    name: "抑菌水",
    categories: ["other"],
    variants: [
      { code: "B12", spec: "10mg×10vials", price: 30, audPrice: 43 },
    ],
  },
];
